import Config from '../../utils/Config';

export interface ICalculatorValues {
    stampDuty: number;
    notarialCosts: number;
    landRegisterCosts: number;
    findersFee: number;
    amortizationRate?: number;
    fixedInterestRate?: number;
}

export interface IEquityValues {
    bank: number;
    stock: number;
    buildingSociety: number;
    assurence: number;
    miscellaneous: number;
}

export interface ICalcParams {
    amortizationRateList: number[];
    fixedInterestRateList: number[];
    rates: { [key: string]: number[] };
    addition: { [key: string]: number };
}

const defaultCalcParams: ICalcParams = {
    // Select Tilgungsrate
    amortizationRateList: [2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5],

    // Select Laufzeit geb. Sollzins
    fixedInterestRateList: [5, 10, 15, 20],

    // Basiszins in % je fixedInterestRateList je Verkehrswertauslauf
    rates: {
        '50': [2.64, 2.74, 2.94, 3.24],
        '60': [2.64, 2.74, 2.94, 3.24],
        '70': [2.64, 2.74, 2.94, 3.24].map((i) => i + 0.05),
        '80': [2.64, 2.74, 2.94, 3.24].map((i) => i + 0.1),
        '85': [2.64, 2.74, 2.94, 3.24].map((i) => i + 0.35),
        '90': [2.64, 2.74, 2.94, 3.24].map((i) => i + 0.5),
        '95': [2.64, 2.74, 2.94, 3.24].map((i) => i + 0.85),
        '100': [2.64, 2.74, 2.94, 3.24].map((i) => i + 1.5),
    },

    // Volumenaufschlag/abschlag in % je Volumen
    addition: {
        '50000': 0.1,
        '150000': 0,
        '200000': -0.1,
        '300000': -0.2,
    },
};

const configCalcParams = Config.getDrupalVar('propType.loanCalculator.calcParams');

class Calculator {
    defaultPercentage: ICalculatorValues = {
        stampDuty: 5, // Grunderwerbssteuer
        notarialCosts: 1.5, // Notarkosten
        landRegisterCosts: 0.5, // Grundbucheitrag
        findersFee: 6.25, // Maklerprovision
        amortizationRate: 2.5, // Tilgungsrate
        fixedInterestRate: 10, //Sollzinsbindung in Jahren
    };

    defaultEquityPercentage = {
        bank: 20,
        stock: 0,
        buildingSociety: 0,
        assurence: 0,
        miscellaneous: 0,
    };

    rates: ICalculatorValues = null;
    calcParams: ICalcParams = null;

    // interestRatePercentage: number = 0.96; // Sollzins

    price: number = null; // Kaufpreis
    closingCostsPercentage: number = null; // Kaufnebenkosten in Prozent
    closingCosts: number = null; // Kaufnebenkosten

    equity: number = null; // Eigenkapital
    equityValues: IEquityValues = null;
    // equityBank: number = null;
    // equityStock: number = null;
    // equityBuildingSociety: number = null;
    // equityAssurence: number = null;
    // equityMiscellaneous: number = null;

    constructor(
        price: number,
        equity: number = null,
        calcPercentages: ICalculatorValues = null,
        equityValues: IEquityValues = null,
        calcParams: ICalcParams = null
    ) {
        this.calcParams = { ...defaultCalcParams, ...configCalcParams, ...calcParams };

        this.price = price;
        // this.equity = equity === null ? price / 5 : equity;

        this.equityValues = this.defaultEquityPercentage; // fake values, will be overwritten in foreach
        this.equity = 0;
        Object.keys(this.defaultEquityPercentage).forEach((k) => {
            const value = equityValues && typeof equityValues[k] === 'number' ? equityValues[k] : (this.price / 100) * this.defaultEquityPercentage[k];
            this.equityValues[k] = value;
            this.equity += value;
        });

        this.rates = this.defaultPercentage;
        Object.keys(this.defaultPercentage).forEach((k) => {
            this.rates[k] = calcPercentages && typeof calcPercentages[k] === 'number' ? calcPercentages[k] : this.defaultPercentage[k];
        });
        this.closingCostsPercentage = this.rates.stampDuty + this.rates.notarialCosts + this.rates.landRegisterCosts + this.rates.findersFee;
        this.closingCosts = (this.closingCostsPercentage / 100) * this.price;
    }

    /**
     * Die zu finanzierende Summe
     */
    public getKreditsumme = (): number => {
        return this.closingCosts + this.price - this.equity;
    };

    /**
     * Die Annuität ist konstant über den Kreditverlauf
     */
    public getAnnuitaet = (): number => {
        const KS = this.getKreditsumme();

        const tilungsrate = this.getTilgungsrate() / 100;
        const effektivzins = this.getEffektivzins() / 100;
        return KS * (tilungsrate + effektivzins);
    };

    /**
     * monatliche Rate
     */
    public getMonatsrate = (): number => {
        return this.getAnnuitaet() / 12;
    };

    /**
     * Nettodarlehen / Summe mit Nebenkosten
     */
    public getNettodarlehen = (): number => {
        return this.closingCosts + this.price - this.equity;
    };

    /**
     * Kaufpreis
     */
    public getPreis = (): number => {
        return this.price;
    };

    /**
     * Sollzins
     */
    public getSollzins = (): number => {
        // Zins Indexe
        const currRate = 100 - (this.equity / this.price) * 100;
        const key = Object.keys(this.calcParams.rates).find((rate) => currRate <= parseFloat(rate));
        const FIRateIdx = this.calcParams.fixedInterestRateList.findIndex((val) => val === this.rates.fixedInterestRate);

        // Volumenauf/abschlag
        const netto = this.getNettodarlehen();
        let addRate = 0;
        Object.keys(this.calcParams.addition).find((price) => {
            addRate = this.calcParams.addition[parseFloat(price)];
            return netto <= parseFloat(price);
        });

        return this.calcParams.rates && this.calcParams.rates[key] && this.calcParams.rates[key][FIRateIdx]
            ? this.calcParams.rates[key][FIRateIdx] + addRate
            : addRate;
    };

    /**
     * Effektivzins in %
     *
     * Manche Banken schlagen Gebühren auf den Sollzins drauf, wenn der Kunde einen höheren, anfänglichen Tilgungssatz
     * wünscht. Beim Effektivzins werden diese Kosten eingerechnet. Auch werden je nach
     * Vertrag Zinsen auf bereits getilgte Summen geszahlt. Dies wird im Effektivzins
     * getEffektivzins.
     * https://www.drklein.de/sollzins-effektivzins-faq.html
     */
    public getEffektivzins = (): number => {
        // TODO: Mit der Bank abklären.  Immoscout scheint auf niedrige Tilgungsraten einen Aufschlag von 0.01% zu geben
        // https://www.immobilienscout24.de/baufinanzierung/finanzierungsrechner/
        // Immoscout nimmt trotzdem den Sollzins für die Berechnung.
        return this.getSollzins();
    };

    /**
     * Kaufnebenkosten
     */
    public getKaufnebenkostenValue = (): number => {
        return this.closingCosts;
    };

    /**
     * Kaufnebenkosten Percentage
     */
    public getKaufnebenkosten = (): number => {
        return this.closingCostsPercentage;
    };

    /**
     * Grunderwerbssteuer
     */
    public getGrunderwerbssteuer = (): number => {
        return this.rates.stampDuty;
    };

    /**
     * Notarkosten
     */
    public getNotarkosten = (): number => {
        return this.rates.notarialCosts;
    };

    /**
     * Grundbucheintrag
     */
    public getGrundbucheintrag = (): number => {
        return this.rates.landRegisterCosts;
    };

    /**
     * Maklerprovision
     */
    public getMaklerprovision = (): number => {
        return this.rates.findersFee;
    };

    /**
     * Tilgungsrate in Prozent
     */
    public getTilgungsrate = (): number => {
        return this.rates.amortizationRate;
    };

    /**
     * Sollzinsbindung in Jahren
     */
    public getSollzinsbindung = (): number => {
        return this.rates.fixedInterestRate;
    };

    // public get(key: 'price' | 'interest' | 'equity' | 'closingCosts' | keyof ICalculatorValues) {
    //     return this[key] || this.values[key];
    // }

    /**
     * Summe der Annuitäten
     */
    public getRatensumme = (): number => {
        // n : Zinsbindungszeit in Jahren
        const n = this.getSollzinsbindung();
        return n * this.getAnnuitaet();
    };

    /**
     * Summe der gezahlten Zinsen
     */
    public getZinssumme = (): number => {
        return this.getRatensumme() - this.getGetilgt();
    };

    /**
     * Summe der Tilgungen
     */
    public getGetilgt = (): number => {
        // n : Zinsbindungszeit in Monaten
        const n = this.getSollzinsbindung() * 12;
        const KS = this.getKreditsumme();
        const tilungsrate = this.getTilgungsrate() / (12 * 100);
        const effektivzins = this.getEffektivzins() / (12 * 100);
        const T1 = KS * tilungsrate;
        var summeTilgung = T1;
        for (var i = 2; i <= n; i++) {
            const Ti = T1 * Math.pow(1 + effektivzins, i - 1);
            summeTilgung += Ti;
        }

        return summeTilgung;
    };

    /**
     * Restschuld nach der Zinsbindung
     */
    public getRestschuld = (): number => {
        const KS = this.getKreditsumme();
        const GS = this.getGetilgt();
        return KS - GS;
    };

    /**
     * Eigenkapital
     */
    public getEigenkapital = (): number => {
        return Math.round(this.equity * 100) / 100;
    };

    public getBankguthaben = (): number => {
        return this.equityValues.bank;
    };
    public getWertpapiere = (): number => {
        return this.equityValues.stock;
    };
    public getBausparguthaben = (): number => {
        return this.equityValues.buildingSociety;
    };
    public getLebensversicherung = (): number => {
        return this.equityValues.assurence;
    };
    public getSonstiges = (): number => {
        return this.equityValues.miscellaneous;
    };

    /**
     * select options
     */
    public getAmortizationRateList = (): number[] => {
        return this.calcParams.amortizationRateList;
    };

    public getFixedInterestRateList = (): number[] => {
        return this.calcParams.fixedInterestRateList;
    };
}

export default Calculator;
